import { cn, focusRing } from "@simpro/ui";
import { useQuery } from "@tanstack/react-query";
import { X } from "lucide-react";
import { useState } from "react";
import { page } from "@/shared/styles/layout";
import { announcementsQuery } from "../api/salon.queries";

const DISMISSED_KEY = "salon.dismissedAnnouncements";

function readDismissed(): string[] {
  try {
    const parsed: unknown = JSON.parse(localStorage.getItem(DISMISSED_KEY) ?? "[]");
    return Array.isArray(parsed) ? parsed.filter((id): id is string => typeof id === "string") : [];
  } catch {
    return [];
  }
}

function writeDismissed(ids: readonly string[]): void {
  try {
    localStorage.setItem(DISMISSED_KEY, JSON.stringify(ids));
  } catch {
    // Storage can be unavailable (private mode); the card is then dismissed for this visit only.
  }
}

/**
 * What is on at the salon (design note salon-booking-design-2.md H1): a line after the hero, never
 * above it, that the customer can dismiss; it stays dismissed on this device, per message. Nothing at
 * all when there are none. Drawn as a pale sage pill with the board's breathing sage dot (design note
 * salon-booking-organic-wellness.md section 6 item 3).
 */
export function AnnouncementsBand() {
  const { data: announcements } = useQuery(announcementsQuery());
  const [dismissed, setDismissed] = useState(readDismissed);
  const shown = (announcements ?? []).filter(
    (announcement) => !dismissed.includes(announcement.id),
  );
  if (shown.length === 0) return null;

  const dismiss = (id: string) => {
    const next = [...dismissed, id];
    setDismissed(next);
    writeDismissed(next);
  };

  return (
    <section aria-label="What is on" className={cn(page, "flex flex-col gap-2.5")}>
      {shown.map((announcement) => (
        <div
          key={announcement.id}
          className="animate-arrive grid grid-cols-[auto_1fr_auto] items-center gap-4 rounded-card bg-muted-bg py-2.5 pr-2 pl-5 md:rounded-full text-sm text-content lg:text-[15px]"
        >
          <span aria-hidden="true" className="animate-breathe size-2.5 rounded-full bg-sage" />
          <p className="leading-snug">
            <span className="mr-2 hidden font-medium text-subtle sm:inline">News ·</span>
            <b className="font-semibold text-heading">{announcement.title}</b> {announcement.body}
          </p>
          <button
            type="button"
            aria-label={`Dismiss: ${announcement.title}`}
            onClick={() => dismiss(announcement.id)}
            className={cn(
              "grid size-10 place-items-center rounded-full text-muted hover:bg-muted-bg hover:text-heading",
              focusRing,
            )}
          >
            <X className="size-5" aria-hidden />
          </button>
        </div>
      ))}
    </section>
  );
}
